import { Card, Col, Flex, Form, message, Row, Skeleton, Spin } from 'antd';
import { BaseButton } from 'components/button/button-base';
import { ErrorListDisplay } from 'components/ErrorDisplay';
import { Text, Title } from 'components/mvp-typography';
import { format } from 'date-fns';
import { UserFilledIcon } from 'features/patient/onboarding/components/form-profile/components/user-filled-icon';
import { clinicInfoEditRequestImageUploadURL } from 'lib/api/firebase/apis/apis';
import { storage } from 'lib/api/firebase/firebase-api';
import {
  useGetClinicAllInfo,
  useMutateClinicInfo
} from 'lib/hooks/tanstack/use-getClinicInfo';
import { useClinicianContext } from 'lib/hooks/use-resolveCurrentClinicInfo';
import { halfCol, ROW_GUTTER } from 'lib/utils/antd';
import { resolveError } from 'lib/utils/formatters/error/resolve-error';
import { useErrorList } from 'lib/utils/useErrorList';
import moment, { isMoment, Moment } from 'moment';
import { useEffect } from 'react';
import { Clinic } from '../../../lib/documents/clinic';
import { UploadImageButton } from './_components/button-upload-image';
import {
  QUESTIONS_CLINIC_EDIT,
  QUESTIONS_CLINIC_EDIT_PAYMENTCONFIG
} from './_components/form-questions';
import { FormQuestionGroupDisplay } from './display-form-question';
/**
 * This component allows ClinicAdmins to edit clinic information
 */
export default function ClinicInfoEdit() {
  const clinicianContext = useClinicianContext();
  const errorList = useErrorList();
  const [form] = Form.useForm();

  const { clinicInfo, ...clinicInfoQuery } = useGetClinicAllInfo(
    clinicianContext.currentClinic
  );

  const mutateClinicInfo = useMutateClinicInfo({
    onSuccess: () => {
      message.success('Updated Clinic information');
    }
  });

  /**
   * Update the form when the clinic info field changes
   * This forces the form to reset and use the new clinic data for the initial form data
   */
  useEffect(() => {
    form.resetFields();
  }, [clinicInfo?.clinic.id, form]);

  async function onFormSubmit(values: Record<string, any>) {
    // Return early if the clinic information is not ready
    if (!clinicInfo) return;
    errorList.reset();
    try {
      const { logo, ...otherValues } = values;
      console.debug(values);
      const clinic = {
        ...otherValues,
        id: clinicInfo.clinic.id
      };

      // If the logo is specified
      if (logo) {
        await uploadLogo(clinic.id, logo.file);
      } else {
        // The logo is unset
        // Delete the data in the database
        // await deleteLogo(clinic.id);
      }

      // Save the data
      // WARN: Typecast to any because the `clinic` object has not been validated
      // This is due to a complexity in the `crx_settings` object. In the short term only the backend will validate the object
      await mutateClinicInfo.mutateAsync(clinic as any);
    } catch (e) {
      const error = resolveError(e);
      errorList.addError(error);
    }

    async function uploadLogo(clinicId: string, logo: File) {
      if (!logo) throw new Error('The logo was not loaded');
      try {
        const {
          data: { url: uploadURL }
        } = await clinicInfoEditRequestImageUploadURL({
          clinicId: clinicId,
          fileType: 'png'
        });
        const ref = storage.refFromURL(uploadURL);
        await ref.put(logo);
      } catch (e) {
        const error = resolveError(e);
        const newError = new Error(`Failed to upload logo. ${error.message}`);
        newError.cause = error;
        errorList.addError(newError, '[uploadLogo]');
        throw newError;
      }
    }
  }

  if (clinicInfo === null || clinicInfoQuery.query.isLoading) {
    return (
      <Flex justify="center" align="center" vertical>
        <Flex gap={16} align="center">
          <Title level={3}>Fetching clinic data</Title>
          <Spin spinning size="large" />
        </Flex>
        <Skeleton active />
      </Flex>
    );
  }

  /**
   * TODO: Remove this when Task 474 is implemented
   */
  const updateDate: Moment | null =
    clinicInfo.clinic.metadata?.updated === undefined
      ? null
      : isMoment(clinicInfo.clinic.metadata?.updated)
      ? clinicInfo.clinic.metadata?.updated
      : moment(clinicInfo.clinic.metadata?.updated);

  return (
    <Card>
      <Flex vertical justify={'center'} align={'center'}>
        <Title>Lets Set Up Your Clinic</Title>

        <div style={{ width: '100%' }}>
          <Form
            key={clinicInfo.clinic.id}
            form={form}
            initialValues={{
              ...clinicInfo.clinic
            }}
            onFinish={onFormSubmit}
            style={{
              width: '100%'
            }}
          >
            {errorList.hasErrors && <ErrorListDisplay errorList={errorList} />}
            <Row gutter={ROW_GUTTER}>
              <Col {...halfCol}></Col>
              <Col {...halfCol}>
                <Flex align="end" vertical>
                  <div>
                    <BaseButton
                      style={{ marginTop: '40px' }}
                      type="primary"
                      htmlType="submit"
                      disabled={mutateClinicInfo.status === 'loading'}
                      loading={mutateClinicInfo.status === 'loading'}
                    >
                      Save
                    </BaseButton>
                  </div>
                  {updateDate && (
                    <Text
                      style={{
                        fontSize: 12
                      }}
                    >
                      Last updated:{' '}
                      {format(updateDate.toDate(), 'yyyy MMM dd HH:mm OOOO')}
                    </Text>
                  )}
                </Flex>
              </Col>
            </Row>

            <Row
              gutter={ROW_GUTTER}
              style={{
                width: '100%'
              }}
            >
              <ColumnLeft />
              <ColumnRight clinicInfo={clinicInfo} />
            </Row>
          </Form>
        </div>
      </Flex>
    </Card>
  );
}

function ColumnLeft() {
  return (
    <Col {...halfCol}>
      <Flex vertical gap={16}>
        <FormQuestionGroupDisplay
          title="Clinic Details"
          questions={QUESTIONS_CLINIC_EDIT}
        />
        <FormQuestionGroupDisplay
          title="Payment Config"
          questions={QUESTIONS_CLINIC_EDIT_PAYMENTCONFIG}
        />
      </Flex>
    </Col>
  );
}

function ColumnRight(props: {
  clinicInfo: { clinic: Clinic; altLink?: string };
}) {
  return (
    <Col {...halfCol}>
      <Text style={{ fontWeight: 'bold' }}>Clinic Logo</Text>
      <div
        style={{
          display: 'flex'
        }}
      >
        <Card
          styles={{
            body: {
              padding: '1rem'
            }
          }}
        >
          {props.clinicInfo.altLink && (
            <img
              style={{
                minWidth: 100,
                minHeight: 100,
                maxWidth: 300,
                maxHeight: 300
              }}
              src={props.clinicInfo.altLink}
              alt={`${props.clinicInfo.clinic.name}'s logo`}
            />
          )}
          <Form.Item
            name="logo"
            required
            style={{
              marginBottom: 0
            }}
          >
            <UploadImageButton text="Upload Logo" icon={<UserFilledIcon />} />
          </Form.Item>
        </Card>
      </div>
    </Col>
  );
}
